import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield,
  Users,
  CheckCircle2,
  XCircle,
  Ban,
  Clock,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Search,
  ChevronRight,
  Eye,
  MessageSquare,
  Crown,
  UserCheck,
  UserX,
  Mail,
  Building2,
  Phone,
  Globe,
  Calendar,
  TrendingUp,
  Database,
  Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  active: { label: 'Active', color: 'text-green-400', bg: 'bg-green-500/10', icon: CheckCircle2 },
  suspended: { label: 'Suspended', color: 'text-amber-400', bg: 'bg-amber-500/10', icon: XCircle },
  banned: { label: 'Banned', color: 'text-red-400', bg: 'bg-red-500/10', icon: Ban },
  pending: { label: 'Pending', color: 'text-blue-400', bg: 'bg-blue-500/10', icon: Clock }
};

const tierConfig = {
  demo: { label: 'Demo', color: 'text-orange-400', bg: 'bg-orange-500/10' },
  standard: { label: 'Standard', color: 'text-sky-400', bg: 'bg-sky-500/10' },
  premium: { label: 'Premium', color: 'text-yellow-400', bg: 'bg-yellow-500/10' }
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dashboard stats
  const [stats, setStats] = useState(null);
  
  // Users
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSearch, setUserSearch] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  
  // Signup requests
  const [signupRequests, setSignupRequests] = useState([]);
  
  // Selected user for details
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  
  // Action modals
  const [actionModal, setActionModal] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/check`, {
        withCredentials: true
      });
      
      if (response.data.is_admin) {
        setIsAdmin(true);
        fetchDashboardData();
      } else {
        toast.error('Admin access required');
        navigate('/client/login');
      }
    } catch (error) {
      console.error('Admin check failed:', error);
      toast.error('Please log in as an admin');
      navigate('/client/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Stagger API calls to avoid rate limiting
      const statsRes = await axios.get(`${BACKEND_URL}/api/admin/stats`, { withCredentials: true });
      setStats(statsRes.data);
      
      // Small delay between calls
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const usersRes = await axios.get(`${BACKEND_URL}/api/admin/users?limit=50`, { withCredentials: true });
      setUsers(usersRes.data.users || []);
      setUsersTotal(usersRes.data.total || 0);
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const signupsRes = await axios.get(`${BACKEND_URL}/api/admin/signup-requests?status=pending`, { withCredentials: true });
      setSignupRequests(signupsRes.data.requests || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    }
  };

  const fetchUserDetails = async (customerId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users/${customerId}`, {
        withCredentials: true
      });
      setUserDetails(response.data);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const handleStatusChange = async (customerId, newStatus) => {
    setProcessing(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/users/${customerId}/status`, {
        status: newStatus,
        reason: actionReason
      }, { withCredentials: true });
      
      toast.success(`User status changed to ${newStatus}`);
      setActionModal(null);
      setActionReason('');
      fetchDashboardData();
      
      if (userDetails?.user?.customer_id === customerId) {
        fetchUserDetails(customerId);
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setProcessing(false);
    }
  };

  const handleSignupApproval = async (requestId, approved) => {
    setProcessing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/signup-requests/${requestId}/approve`, {
        approved,
        rejection_reason: approved ? null : actionReason
      }, { withCredentials: true });
      
      toast.success(approved ? 'Signup approved!' : 'Signup rejected');
      setActionModal(null);
      setActionReason('');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to process signup');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteUser = async (customerId, userEmail) => {
    setProcessing(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${customerId}`, {
        withCredentials: true
      });
      
      toast.success(`User ${userEmail} deleted successfully`);
      setActionModal(null);
      setSelectedUser(null);
      setUserDetails(null);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setProcessing(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !userSearch || 
      user.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.business_name?.toLowerCase().includes(userSearch.toLowerCase());
    const matchesFilter = userFilter === 'all' || user.status === userFilter;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Admin Panel</h1>
              <p className="text-sm text-slate-400">Alaska AI Services</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchDashboardData}
              className="border-slate-700 text-slate-300"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/client/dashboard')}
              className="border-slate-700 text-slate-300"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 flex-wrap">
          <Button
            variant={activeTab === 'dashboard' ? 'default' : 'outline'}
            onClick={() => setActiveTab('dashboard')}
            className={activeTab === 'dashboard' ? 'bg-sky-500 hover:bg-sky-600' : 'border-slate-700'}
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <Button
            variant={activeTab === 'users' ? 'default' : 'outline'}
            onClick={() => setActiveTab('users')}
            className={activeTab === 'users' ? 'bg-sky-500 hover:bg-sky-600' : 'border-slate-700'}
          >
            <Users className="w-4 h-4 mr-2" />
            Users ({usersTotal})
          </Button>
          <Button
            variant={activeTab === 'signups' ? 'default' : 'outline'}
            onClick={() => setActiveTab('signups')}
            className={activeTab === 'signups' ? 'bg-sky-500 hover:bg-sky-600' : 'border-slate-700'}
          >
            <UserCheck className="w-4 h-4 mr-2" />
            Signup Requests
            {signupRequests.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {signupRequests.length}
              </span>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/admin/knowledge')}
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
          >
            <Database className="w-4 h-4 mr-2" />
            Knowledge Base
          </Button>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-sky-400" />
                  <span className="text-slate-400 text-sm">Total Users</span>
                </div>
                <div className="text-3xl font-bold">{stats.users.total}</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
              >
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span className="text-slate-400 text-sm">Active</span>
                </div>
                <div className="text-3xl font-bold text-green-400">{stats.users.active}</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-5 h-5 text-blue-400" />
                  <span className="text-slate-400 text-sm">Pending Signups</span>
                </div>
                <div className="text-3xl font-bold text-blue-400">{stats.signups.pending}</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-slate-900/50 border border-slate-800 rounded-xl p-5"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Crown className="w-5 h-5 text-yellow-400" />
                  <span className="text-slate-400 text-sm">Premium</span>
                </div>
                <div className="text-3xl font-bold text-yellow-400">{stats.subscriptions.premium}</div>
              </motion.div>
            </div>

            {/* Subscription Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
                <h3 className="font-semibold mb-4">Subscription Tiers</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-orange-500/10 rounded-lg">
                    <span className="text-orange-400">Demo</span>
                    <span className="font-bold">{stats.subscriptions.demo}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-sky-500/10 rounded-lg">
                    <span className="text-sky-400">Standard</span>
                    <span className="font-bold">{stats.subscriptions.standard}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-yellow-500/10 rounded-lg">
                    <span className="text-yellow-400">Premium</span>
                    <span className="font-bold">{stats.subscriptions.premium}</span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
                <h3 className="font-semibold mb-4">User Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg">
                    <span className="text-green-400">Active</span>
                    <span className="font-bold">{stats.users.active}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-amber-500/10 rounded-lg">
                    <span className="text-amber-400">Suspended</span>
                    <span className="font-bold">{stats.users.suspended}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg">
                    <span className="text-red-400">Banned</span>
                    <span className="font-bold">{stats.users.banned}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            {stats.recent_actions?.length > 0 && (
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-800">
                  <h3 className="font-semibold">Recent Admin Actions</h3>
                </div>
                <div className="divide-y divide-slate-800 max-h-[300px] overflow-y-auto">
                  {stats.recent_actions.map((action) => (
                    <div key={`${action.timestamp}-${action.target_email}`} className="px-6 py-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-300">{action.action}</span>
                        <span className="text-slate-500">{new Date(action.timestamp).toLocaleString()}</span>
                      </div>
                      <div className="text-slate-500 text-xs">
                        {action.target_email} by {action.admin_email}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search and Filter */}
            <div className="flex gap-4 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search by email or business name..."
                  className="pl-10 bg-slate-800/50 border-slate-700"
                />
              </div>
              <select
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="banned">Banned</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            {/* Users List */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
              <div className="divide-y divide-slate-800">
                {filteredUsers.map((user) => {
                  const status = statusConfig[user.status] || statusConfig.active;
                  const tier = tierConfig[user.subscription_tier] || tierConfig.demo;
                  const StatusIcon = status.icon;
                  
                  return (
                    <div
                      key={user.customer_id}
                      className="px-6 py-4 hover:bg-slate-800/50 cursor-pointer"
                      onClick={() => { setSelectedUser(user); fetchUserDetails(user.customer_id); }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full ${status.bg} flex items-center justify-center`}>
                            <StatusIcon className={`w-5 h-5 ${status.color}`} />
                          </div>
                          <div>
                            <div className="font-medium">{user.business_name || 'No Business Name'}</div>
                            <div className="text-sm text-slate-400">{user.email}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-xs ${tier.bg} ${tier.color}`}>
                            {tier.label}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs ${status.bg} ${status.color}`}>
                            {status.label}
                          </span>
                          <ChevronRight className="w-4 h-4 text-slate-600" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Signup Requests Tab */}
        {activeTab === 'signups' && (
          <div className="space-y-6">
            {signupRequests.length === 0 ? (
              <div className="text-center py-16 bg-slate-900/50 border border-slate-800 rounded-xl">
                <UserCheck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-400 mb-2">No pending requests</h3>
                <p className="text-slate-500 text-sm">All signup requests have been processed</p>
              </div>
            ) : (
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
                <div className="divide-y divide-slate-800">
                  {signupRequests.map((request) => (
                    <div key={request.request_id} className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-lg">{request.business_name}</h3>
                          <p className="text-slate-400">{request.email}</p>
                        </div>
                        <span className="px-3 py-1 bg-blue-500/10 text-blue-400 text-xs rounded-full">
                          Pending Review
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                        {request.phone && (
                          <div className="flex items-center gap-2 text-slate-400">
                            <Phone className="w-4 h-4" />
                            {request.phone}
                          </div>
                        )}
                        {request.website && (
                          <div className="flex items-center gap-2 text-slate-400">
                            <Globe className="w-4 h-4" />
                            <a href={request.website} target="_blank" rel="noopener noreferrer" className="hover:text-sky-400">
                              {request.website}
                            </a>
                          </div>
                        )}
                        {request.business_type && (
                          <div className="flex items-center gap-2 text-slate-400">
                            <Building2 className="w-4 h-4" />
                            {request.business_type}
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-slate-400">
                          <Calendar className="w-4 h-4" />
                          {new Date(request.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      
                      {request.business_description && (
                        <p className="text-sm text-slate-300 mb-4 p-3 bg-slate-800/50 rounded-lg">
                          {request.business_description}
                        </p>
                      )}
                      
                      <div className="flex gap-3">
                        <Button
                          onClick={() => handleSignupApproval(request.request_id, true)}
                          disabled={processing}
                          className="bg-green-500 hover:bg-green-600"
                        >
                          <UserCheck className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setActionModal({ type: 'reject_signup', request })}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <UserX className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* User Details Sidebar */}
      {selectedUser && userDetails && (
        <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-slate-900 border-l border-slate-800 shadow-xl z-50 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">User Details</h2>
              <Button variant="ghost" onClick={() => { setSelectedUser(null); setUserDetails(null); }}>
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="space-y-6">
              {/* User Info */}
              <div className="p-4 bg-slate-800/50 rounded-xl">
                <h3 className="font-semibold mb-3">{userDetails.user.business_name || 'No Business Name'}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-400">
                    <Mail className="w-4 h-4" />
                    {userDetails.user.email}
                  </div>
                  {userDetails.user.phone && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Phone className="w-4 h-4" />
                      {userDetails.user.phone}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-3">
                  <span className={`px-3 py-1 rounded-full text-xs ${statusConfig[userDetails.user.status]?.bg} ${statusConfig[userDetails.user.status]?.color}`}>
                    {statusConfig[userDetails.user.status]?.label}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs ${tierConfig[userDetails.user.subscription_tier]?.bg} ${tierConfig[userDetails.user.subscription_tier]?.color}`}>
                    {tierConfig[userDetails.user.subscription_tier]?.label}
                  </span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                  <div className="text-2xl font-bold">{userDetails.stats.leads_count}</div>
                  <div className="text-xs text-slate-400">Leads</div>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                  <div className="text-2xl font-bold">{userDetails.stats.login_count}</div>
                  <div className="text-xs text-slate-400">Logins</div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm text-slate-400">Actions</h4>
                {userDetails.user.status === 'active' && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setActionModal({ type: 'suspend', user: userDetails.user })}
                      className="w-full justify-start border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Suspend User
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setActionModal({ type: 'ban', user: userDetails.user })}
                      className="w-full justify-start border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Ban User
                    </Button>
                  </>
                )}
                {userDetails.user.status === 'suspended' && (
                  <>
                    <Button
                      onClick={() => handleStatusChange(userDetails.user.customer_id, 'active')}
                      className="w-full justify-start bg-green-500 hover:bg-green-600"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Reactivate User
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setActionModal({ type: 'ban', user: userDetails.user })}
                      className="w-full justify-start border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Ban User
                    </Button>
                  </>
                )}
                {userDetails.user.status === 'banned' && (
                  <div className="p-3 bg-red-500/10 rounded-lg text-center">
                    <p className="text-red-400 text-sm">This user has been permanently banned</p>
                    {userDetails.user.ban_reason && (
                      <p className="text-xs text-slate-400 mt-1">Reason: {userDetails.user.ban_reason}</p>
                    )}
                  </div>
                )}
                
                {/* Delete User - Available for all statuses */}
                <div className="pt-2 border-t border-slate-700 mt-2">
                  <Button
                    variant="outline"
                    onClick={() => setActionModal({ type: 'delete', user: userDetails.user })}
                    className="w-full justify-start border-red-700/50 text-red-500 hover:bg-red-500/10"
                    data-testid="delete-user-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete User Permanently
                  </Button>
                </div>
              </div>

              {/* Audit Log */}
              {userDetails.audit_log?.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm text-slate-400 mb-2">Activity Log</h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {userDetails.audit_log.map((log) => (
                      <div key={`${log.timestamp}-${log.action}`} className="p-2 bg-slate-800/30 rounded text-xs">
                        <div className="text-slate-300">{log.action}</div>
                        <div className="text-slate-500">{new Date(log.timestamp).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {actionModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-6"
          >
            <h3 className="text-lg font-bold mb-4">
              {actionModal.type === 'suspend' && 'Suspend User'}
              {actionModal.type === 'ban' && 'Ban User'}
              {actionModal.type === 'reject_signup' && 'Reject Signup'}
              {actionModal.type === 'delete' && 'Delete User Permanently'}
            </h3>
            
            <p className="text-slate-400 text-sm mb-4">
              {actionModal.type === 'suspend' && `Suspend ${actionModal.user?.email}? They will not be able to log in until reactivated.`}
              {actionModal.type === 'ban' && `Permanently ban ${actionModal.user?.email}? This cannot be undone and no refunds will be issued.`}
              {actionModal.type === 'reject_signup' && `Reject signup request from ${actionModal.request?.email}?`}
              {actionModal.type === 'delete' && (
                <span className="text-red-400">
                  <strong>WARNING:</strong> This will permanently delete {actionModal.user?.email} and ALL their data including leads, appointments, and reviews. This action cannot be undone!
                </span>
              )}
            </p>
            
            {actionModal.type !== 'delete' && (
              <div className="mb-4">
                <label className="block text-sm text-slate-400 mb-1">Reason (optional)</label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  rows={3}
                  placeholder="Enter reason for this action..."
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white resize-none"
                />
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => { setActionModal(null); setActionReason(''); }}
                className="flex-1 border-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (actionModal.type === 'suspend') {
                    handleStatusChange(actionModal.user.customer_id, 'suspended');
                  } else if (actionModal.type === 'ban') {
                    handleStatusChange(actionModal.user.customer_id, 'banned');
                  } else if (actionModal.type === 'reject_signup') {
                    handleSignupApproval(actionModal.request.request_id, false);
                  } else if (actionModal.type === 'delete') {
                    handleDeleteUser(actionModal.user.customer_id, actionModal.user.email);
                  }
                }}
                disabled={processing}
                className={`flex-1 ${actionModal.type === 'ban' || actionModal.type === 'delete' ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'}`}
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
